import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { downloadFile } from "helpers/common";
import { exportFleetVehiclesAPI } from "services/api/fleet/vehicles/api";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { initialPagination } from "ui/components/Table/consts";
import { Pagination } from "services/search";
import { VehicleExportModalProps } from "./types";
import { FLEET_VEHICLE_EXPORT_COLUMNS } from "./consts";
import { NoResults } from "ui/components/NoResults";
import { Badge } from "ui/components/Badge";

const VehicleExportModal: React.FC<VehicleExportModalProps> = (props) => {
  const { show, selectedVehicleIds, vehicles, onClose, onExportSuccess } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState(false);

  // Lokalna selekcija (checkboxovi) — kreće od svih odabranih iz pretrage; korisnik može odselektirati.
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedVehicleIds);
  const [pagination, setPagination] = useState<Pagination>({
    ...initialPagination,
    pageNumber: 1,
    pageSize: 20,
  });

  useEffect(() => {
    if (show) {
      setSelectedIds(selectedVehicleIds);
      setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }
  }, [show, selectedVehicleIds]);

  // Sva vozila odabrana u pretrazi — podloga za prikaz i odznačavanje.
  const selectedVehicles = vehicles.filter((v) =>
    selectedVehicleIds.includes(v.id)
  );

  // Client-side paginacija nad in-memory listom.
  const totalCount = selectedVehicles.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pagination.pageSize));
  const pageData = selectedVehicles.slice(
    (pagination.pageNumber - 1) * pagination.pageSize,
    pagination.pageNumber * pagination.pageSize
  );
  const tablePagination: Pagination = { ...pagination, totalCount, totalPages };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedIds as unknown as number[],
    setSelectedIds as unknown as (items: number[]) => void
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    setPagination(newPagination);
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) return;

    setIsLoading(true);
    try {
      const response = await exportFleetVehiclesAPI(selectedIds);
      if (response) {
        downloadFile(response, "xlsx");
        onExportSuccess();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <ModalIntro
            animation={invoiceAnimation}
            title="Izvoz vozila u Excel"
            description="Odaberite vozila u pretrazi i izvezite ih u Excel datoteku. Datoteku možete urediti i ponovno uvesti u vozni park (round-trip)."
            show={true}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={t("admin.fleet.vehicle.export.title")}
        onHide={onClose}
      />
      <ModalBody>
        <div className="p-8 mt-12 pb-28">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-center text-3xl font-bold text-slate-800 mb-2">
              {t("admin.fleet.vehicle.export.title")}
            </h1>
            <p className="text-center text-md text-slate-600 mx-auto mb-6">
              {t("admin.fleet.vehicle.export.xlsx.description")}
            </p>
          </div>

          {selectedVehicles.length > 0 && (
            <>
              <div className="flex justify-end mb-3">
                <Badge
                  text={`${t("admin.fleet.vehicle.export.selected.count")}: ${selectedIds.length}`}
                  variant="warning"
                />
              </div>

              <div className="min-h-[520px]">
                <TableSearch
                  columns={FLEET_VEHICLE_EXPORT_COLUMNS(t)}
                  data={pageData}
                  selectableRows
                  selectedRows={selectedIds as unknown as number[]}
                  onSelectedChange={
                    handleSelectedChange as unknown as (
                      selected: number[] | number
                    ) => void
                  }
                  pagination={tablePagination}
                  onPaginationChange={handlePaginationChange}
                  size="auto"
                />
              </div>
            </>
          )}
        </div>
      </ModalBody>

      <Loader
        isLoading={isLoading}
        overlay
        message="Izvoz vozila u tijeku..."
        className="fixed inset-y-0 right-0 w-1/2"
      />

      <div className="px-4 py-3 fixed bottom-0 right-0 w-1/2 bg-slate-50 border-t border-slate-200 flex justify-between gap-3">
        <Button variant="outlined" onClick={onClose}>
          {t("admin.cancel")}
        </Button>
        {selectedVehicles.length > 0 && (
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={selectedIds.length === 0 || isLoading}
            loading={isLoading}
          >
            {t("admin.fleet.vehicle.export.button")}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default memo(VehicleExportModal);
